/**
 * Contract terms for the appliance cost calculator.
 *
 * Its own module rather than sitting in `cost.tsx`: this is the part with a
 * rule worth testing, and the test harness cannot load a `.tsx`.
 */

/**
 * Selectable contract terms, in years.
 *
 * Data rather than hand-written branches: the term drives a discount lookup, an
 * option list, a slider per term and a comparison grid, each of which used to
 * carry its own copy of the year numbers - so adding a fifth year meant editing
 * four places in step.
 */
export const CONTRACT_TERMS = [1, 2, 3, 4, 5] as const;

/** The longest term on offer. */
export const MAX_CONTRACT_YEARS = CONTRACT_TERMS[CONTRACT_TERMS.length - 1];

/**
 * Term discount in percent, by contract length. A year the map does not name
 * carries no discount, so the one-year term needs no special case.
 */
export const DEFAULT_TERM_DISCOUNTS: Record<number, number> = {
  2: 5,
  3: 10,
  4: 15,
  5: 20,
};

/** Milliseconds in an average year, leap years included. */
const YEAR_MS = 365.25 * 24 * 60 * 60 * 1000;

/**
 * The contract term the questionnaire implies, from the agreed start and end
 * dates (`general.date.dateStart` / `dateEnd`, parsed onto
 * `common.general.dates`).
 *
 * Rounded to the nearest year, because a term booked 01.01.2026-31.12.2028 is a
 * three-year contract and `Math.floor` would quote and discount it as two.
 *
 * `null` when the questionnaire carries no usable pair of dates - the normal
 * state early on, and also what an end before the start or a term shorter than
 * a year gets. The caller then keeps its own default rather than opening on a
 * term nobody agreed.
 */
export const contractYearsFromDates = (
  start?: string,
  end?: string,
): number | null => {
  if (!start || !end) return null;
  const from = new Date(start).getTime();
  const to = new Date(end).getTime();
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
  const years = Math.round((to - from) / YEAR_MS);
  if (years < 1) return null;
  // A longer contract is offered as the longest term rather than as one the
  // calculator has no discount for.
  return Math.min(years, MAX_CONTRACT_YEARS);
};
